import React, { useState, useEffect } from 'react';
import { Row, Col, Button, ButtonGroup, Modal, Table, Alert } from 'react-bootstrap';

import ValidatedForm from 'components/Form/Inputs';
import { FloatInput } from 'components/Form/Inputs';


const DefaultTierName = 'Tier-default';

const TieredCost = ({ costs, onChange, show, setShow }) => {
  const [tieredCosts, setTieredCosts] = useState([]);
  const [formData, setFormData] = useState();
  const [isEdit, setIsEdit] = useState();
  const [error, setError] = useState();
  const defaultTier = tieredCosts.find(c => c.name === DefaultTierName);
  
  useEffect(() => {
    if (costs) setTieredCosts([...costs]);
  }, costs);

  const handleAdd = () => {
    setIsEdit(false);
    setError();
    newForm();
  };

  const handleEdit = (tierdCost, index) => {
    setIsEdit(true);
    setError();
    setFormData(tierdCost, index);
  };

  const handleDelete = (tieredIndex) => {
    const updated = Array.from(tieredCosts);
    updated.splice(tieredIndex, 1);
    setTieredCosts(updated);
  }

  const openForm = (data) => setFormData(data);

  const closeForm = () => setFormData();

  const newForm = () => openForm({
    idx: tieredCosts.length,
    name: '',
    cost: '',
    min_threshold: '',
    max_threshold: ''
  });

  const upsert = (tieredCost) => {
    const updatedTieredCosts = [...tieredCosts];
    
    if (isEdit) {
      const index = updatedTieredCosts.findIndex(t => t.name === tieredCost.name);
      updatedTieredCosts[index] = tieredCost;
    } else {
      updatedTieredCosts.push(tieredCost);
    }

    setTieredCosts(fixProperties(updatedTieredCosts));
  };

  const fixProperties = (tieredCosts) => {
    let updatedTieredCosts = [...tieredCosts]
    .map(t => ({...t, min_threshold: Number(t.min_threshold), max_threshold: Number(t.max_threshold) }))
    .sort((a, b) => a.min_threshold - b.min_threshold);
    const defaultIndex = updatedTieredCosts.findIndex(t => t.name === DefaultTierName);
    
    if (defaultIndex >= 0) {
      const defaultTier = updatedTieredCosts.splice(defaultIndex, 1)[0];
      updatedTieredCosts = updatedTieredCosts.map((t, i) => ({ ...t, idx: i, name: `Tier-${i + 1}` }));
      updatedTieredCosts.push({ ...defaultTier, idx: updatedTieredCosts.length });
    } else {
      updatedTieredCosts = updatedTieredCosts.map((t, i) => ({ ...t, idx: i, name: `Tier-${i + 1}` }));
    }

    return updatedTieredCosts;
  }

  const handleFormDataChange = (value, field) => {
    setFormData(data => ({ ...data, [field]: value }));
  };

  const isValidFormData = () => {
    const { min_threshold, max_threshold, name } = formData;
    if (!min_threshold && !max_threshold) {
      setError('System size threshold required');
      return false;
    }

    if (min_threshold && max_threshold) {
      if (Number(min_threshold) > Number(max_threshold)) {
        setError('The minimum system size threshold must be smaller than the maximum threshold.');
        return false;
      }
    }

    if (!min_threshold && tieredCosts.some(t => t.name != name && !t.min_threshold)) {
      setError('You can only add one threshold without a minimum threshold.');
      return false;
    } else if (!max_threshold && tieredCosts.some(t => t.name != name && !t.max_threshold)) {
      setError('You can only add one threshold without a maximum threshold.');
      return false;
    }

    return true;
  };

  const handleSaveFormData = (e) => {
    if (!isValidFormData()) {
      return;
    }

    if (!isEdit) {
      const isDefault = e.nativeEvent.submitter.value == 'default';
      if (isDefault) {
        formData['name'] = DefaultTierName;
      }
  
      upsert({
        ...formData,
        name: isDefault ? DefaultTierName : `Tier-${tieredCosts.length + 1}`
      });
    } else {
      upsert(formData);
    }
    closeForm();
    setError();
  };

  const handleSaveAll = () => {
    if (!defaultTier) {
      setError('Default tier pricing is not defined');
      return;
    }

    onChange(tieredCosts);
    closeModal();
  };

  const handleClearAll = () => {
    onChange([]);
    closeModal();
  };

  const closeModal = () => {
    closeForm();
    setTieredCosts(costs ?? []);
    setShow();
    setError();
  };

  const displayThreshold = (min, max) => {
    if (!min) {
      return `${max} or less`;
    }

    if (!max) {
      return `${min} or more`;
    }

    return `${min} to ${max}`;
  };

  return (
    <Modal
      size="lg"
      show={show}
      onHide={() => closeModal()}
      aria-labelledby="contained-modal-title-vcenter"
      centered
    >
      <Modal.Header closeButton>
        <Modal.Title>Tiered Pricing</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p>The Cost per Watt applies to systems up to and including the specified System Size Threshold. Tiers are evaluated in order. If no tier applies, the overage cost per watt will be used.</p>

        <Table>
          <thead>
            <tr>
              <th>Tier</th>
              <th>Cost (per Watt)</th>
              <th>System Size Threshold</th>
              <th>Action</th>
            </tr>
          </thead>
          <tbody>
            {tieredCosts.map((tieredCost, index) => (
              <tr key={tieredCost.idx}>
                <td>{ tieredCost.name }</td>
                <td>{ tieredCost.cost }</td>
                <td>{ displayThreshold(tieredCost.min_threshold, tieredCost.max_threshold) }</td>
                <td>
                  <Button variant="link" onClick={() => handleEdit(tieredCost, index)}>Edit</Button>
                  <Button variant="link" onClick={() => handleDelete(index)}>Delete</Button>
                </td>
              </tr>
            ))}
            {(tieredCosts.length == 0 && !formData) && (
              <tr>
                <td colSpan={4}>There is no tierd costs</td>
              </tr>
            )}
          </tbody>
        </Table>

        {!formData && (
          <Row className="p-3">
            <Button size='sm' onClick={handleAdd}>Add tier</Button>
          </Row>
        )}

        {error && (
          <Row className="p-3">
            <Alert variant="warning">{error}</Alert>
          </Row>
        )}

        {formData && (
          <ValidatedForm onSave={handleSaveFormData}>
            <Col>
              <Row className="mb-3">
                <FloatInput
                  required
                  size="sm"
                  type="number"
                  label="Cost (per Watt)"
                  append="$&nbsp;&nbsp;&nbsp;"
                  value={formData?.cost || ''}
                  onChange={(e) => handleFormDataChange(e.target.value, "cost")}
                />
              </Row>
              <Row className="mb-3">
                <FloatInput
                  size="sm"
                  type="number"
                  label="Minimum System Size (Kw)"
                  append="kW"
                  value={formData?.min_threshold || ''}
                  onChange={(e) => handleFormDataChange(e.target.value, "min_threshold")}
                />
              </Row>
              <Row className="mb-3">
                <FloatInput
                  size="sm"
                  label="Maximum System Size (Kw)"
                  append="kW"
                  value={formData?.max_threshold || ''}
                  onChange={(e) => handleFormDataChange(e.target.value, "max_threshold")}
                />
              </Row>
              <Row className="mb-3">
                {!defaultTier && !isEdit && (
                  <Button className="mr-3" size='sm' variant="outline-primary" type='submit' value="default">Add as default</Button>
                )}

                <ButtonGroup size="sm">
                  <Button variant="outline-primary" type='submit'>{isEdit ? 'Update' : 'Add'}</Button>
                  <Button variant="outline-primary" onClick={() => closeForm()}>Cancel</Button>
                </ButtonGroup>
              </Row>
            </Col>
          </ValidatedForm>
        )}
        
      </Modal.Body>

      <Modal.Footer>
        <Button size="sm" variant="warning" onClick={handleClearAll}>Clear All</Button>
        <span style={{ flex: 1 }}></span>
        <Button size="sm" variant="secondary" onClick={closeModal}>Close</Button>
        <Button size="sm" variant="primary" onClick={handleSaveAll}>Save</Button>
      </Modal.Footer>
    </Modal>
  );
};

export default TieredCost;
